/**
 * ContasReceberPage Component
 * 
 * Página de listagem e gerenciamento de contas a receber.
 * Permite visualizar, criar, editar, excluir e dar baixa em contas a receber.
 * 
 * Funcionalidades:
 * - Listagem de contas a receber com paginação
 * - Filtros por status, cliente, vencimento
 * - Modal de criação/edição
 * - Baixa de contas (marcar como pago)
 * - Cálculo de juros e multa
 * - Exclusão com confirmação
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { 
  Edit, Trash2, Plus, Search, DollarSign, Filter, Download, Upload,
  X, Check, CheckCircle2, AlertCircle, Layers, Sparkles,
  Calendar as CalendarIcon, Clock, Copy, Unlink, FileText, ChartNoAxesGantt, Workflow, FileSearch, User, FileSpreadsheet
} from 'lucide-react'
import { CalendarDatePicker, CalendarRangePicker } from '../../../../components/ERP/Calendar/Calendar'
import ErpPeriodDropdown from '../../../../components/ERP/ErpPeriodDropdown/ErpPeriodDropdown'
import FilterLeftPicker from '../../../../components/ERP/FilterLeftPicker'
import SelectPersonModal from '../../../../components/ERP/Modal/SelectPersonModal'
import AccountsReceivablePdfCanvas from '../../../../components/ERP/Financeiro/AccountsReceivablePdfCanvas'
import { exportElementToPdf } from '../../../../utils/exportElementToPdf'
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { formatDateOnly, parseDateOnly, toDateInputValue } from '../../../../utils/dateUtils'
import '../../../../components/ERP/Financeiro/AccountsReceivablePdfCanvas.css'
import DataTable from '../../../../components/ERP/DataTable/DataTable'
import Modal from '../../../../components/ERP/Modal/Modal'
import FormField from '../../../../components/ERP/Form/FormField'
import { accountReceivableService, customerService, userService, financialAccountService, financialCategoryService, cashStatementService, boletoService } from '../../../../services/erpApi'
import Toast from '../../../../components/Toast/Toast'
import { useToast } from '../../../../hooks/useToast'
import { usePermissions, MODULES, ACTIONS } from '../../../../hooks/usePermissions'
import './ContasReceberPage.css'

const PERSON_TYPE_LABELS = {
  customer: 'Cliente',
  user: 'Funcionário'
}

const RECEIVABLE_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pendente' },
  { value: 'paid', label: 'Pago' },
  { value: 'overdue', label: 'Vencido' },
  { value: 'cancelled', label: 'Cancelado' }
]

function receivableRemaining(conta) {
  if (!conta) return 0
  const amount = Number(conta.amount || 0)
  const discount = Number(conta.discount_amount || 0)
  const interest = Number(conta.interest_amount || 0)
  const fine = Number(conta.fine_amount || 0)
  const paid = Number(conta.paid_amount || 0)
  return Math.max(0, Math.round((amount - discount + interest + fine - paid) * 100) / 100)
}

function FieldDropdown({ value, options, onChange, searchable = false, ariaLabel }) {
  return (
    <ErpPeriodDropdown
      className="contas-receber-field-dropdown"
      label=""
      align="left"
      portal
      searchable={searchable}
      ariaLabel={ariaLabel}
      value={value}
      options={options}
      onChange={onChange}
    />
  )
}

function resolvePersonFields(conta) {
  if (conta.user_id) {
    return {
      personType: 'user',
      personId: String(conta.user_id),
      personName: conta.user_name || ''
    }
  }
  return {
    personType: 'customer',
    personId: conta.customer_id ? String(conta.customer_id) : '',
    personName: conta.customer_name || ''
  }
}

function ContasReceberPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { hasPermission } = usePermissions()
  const canView = hasPermission(MODULES.FINANCIAL, ACTIONS.FINANCIAL_RECEIVABLES_VIEW)
  const canCreate = hasPermission(MODULES.FINANCIAL, ACTIONS.FINANCIAL_RECEIVABLES_CREATE)
  const canUpdate = hasPermission(MODULES.FINANCIAL, ACTIONS.FINANCIAL_RECEIVABLES_UPDATE)
  const canDelete = hasPermission(MODULES.FINANCIAL, ACTIONS.FINANCIAL_RECEIVABLES_DELETE)
  const canMarkPaid = hasPermission(MODULES.FINANCIAL, ACTIONS.FINANCIAL_RECEIVABLES_MARK_PAID)
  const canBoleto = hasPermission(MODULES.FINANCIAL, ACTIONS.FINANCIAL_RECEIVABLES_BOLETO)
  const [contas, setContas] = useState([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [baixaModalOpen, setBaixaModalOpen] = useState(false)
  const [boletoModalOpen, setBoletoModalOpen] = useState(false)
  const [detalhesModalOpen, setDetalhesModalOpen] = useState(false)
  const [contaDetalhes, setContaDetalhes] = useState(null)
  const [editingConta, setEditingConta] = useState(null)
  const [contaParaBaixa, setContaParaBaixa] = useState(null)
  const [contaParaBoleto, setContaParaBoleto] = useState(null)
  const [boletoLoading, setBoletoLoading] = useState(false)
  const [clientes, setClientes] = useState([])
  const [usuarios, setUsuarios] = useState([])
  const [contasFinanceiras, setContasFinanceiras] = useState([])
  const [categoriasReceita, setCategoriasReceita] = useState([])
  const [openMenuId, setOpenMenuId] = useState(null)
  const [menuAnchor, setMenuAnchor] = useState(null)

  // Estados para importação Excel
  const [importModalOpen, setImportModalOpen] = useState(false)
  const [importStep, setImportStep] = useState('upload') // upload | preview | result
  const [importFile, setImportFile] = useState(null)
  const [importPreview, setImportPreview] = useState(null)
  const [importResult, setImportResult] = useState(null)
  const [importLoading, setImportLoading] = useState(false)
  const importFileInputRef = useRef(null)
  
  // Estados para Toast
  const { toast, showToast, hideToast } = useToast()
  
  // Estado para controlar qual código foi copiado
  const [copiedId, setCopiedId] = useState(null)
  const [showPersonModal, setShowPersonModal] = useState(false)
  const [futureCount, setFutureCount] = useState(0)
  const [applyToFuture, setApplyToFuture] = useState(false)
  const [scopePrompt, setScopePrompt] = useState(null)
  const [settlementChoice, setSettlementChoice] = useState(null)
  const [linkReview, setLinkReview] = useState(null)
  const [linkSuggestions, setLinkSuggestions] = useState(null)
  const [linkSuggestionsLoading, setLinkSuggestionsLoading] = useState(false)
  const [linkSwapChoice, setLinkSwapChoice] = useState(null)
  const [workflowOpen, setWorkflowOpen] = useState(false)
  const [workflowAccountId, setWorkflowAccountId] = useState('')
  const [workflowScan, setWorkflowScan] = useState(null)
  const [workflowLoading, setWorkflowLoading] = useState(false)
  const [workflowChoice, setWorkflowChoice] = useState(null)
  const [workflowLinkingId, setWorkflowLinkingId] = useState(null)
  const [formData, setFormData] = useState({
    personType: 'customer',
    personId: '',
    personName: '',
    description: '',
    amount: '',
    dueDate: '',
    status: 'pending',
    categoryId: null,
    costCenterId: null,
    financialAccountId: null,
    interestRate: 0,
    fineRate: 0,
    observations: ''
  })
  const [baixaFormData, setBaixaFormData] = useState({
    paidAmount: '',
    paymentDate: toDateInputValue(new Date()),
    financialAccountId: ''
  })
  const [errors, setErrors] = useState({})
  const [filters, setFilters] = useState({
    search: '',
    status: ['pending', 'overdue'], // Por padrão, mostrar apenas pendentes e vencidos
    customerId: '',
    userId: '',
    originType: '', // 'customer', 'user', ou '' para todos
    dueDateFrom: '',
    dueDateTo: '',
    nextDays: 7, // Por padrão, próximos 7 dias
    lastDays: null
  })
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [isPeriodOpen, setIsPeriodOpen] = useState(false)
  const periodBtnRef = useRef(null)
  const pdfRef = useRef(null)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  })
  const [exportPdfAccounts, setExportPdfAccounts] = useState([])
  const [generatingPDF, setGeneratingPDF] = useState(false)

  const customerFilterOptions = useMemo(
    () => clientes.map((c) => ({ value: String(c.id), label: c.name })),
    [clientes]
  )

  const userFilterOptions = useMemo(
    () => usuarios.map((u) => ({ value: String(u.id), label: u.name })),
    [usuarios]
  )

  const loadContas = useCallback(async () => {
    if (!canView) {
      setContas([])
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const response = await accountReceivableService.getAll({
        ...filters,
        page: pagination.page,
        limit: pagination.limit
      })
      if (response.success) {
        setContas(response.data || [])
        if (response.pagination) {
          setPagination((prev) => ({
            ...prev,
            total: response.pagination.total,
            totalPages: response.pagination.totalPages
          }))
        }
      }
    } catch (error) {
      console.error('Erro ao carregar contas a receber:', error)
    } finally {
      setLoading(false)
    }
  }, [filters, pagination.page, pagination.limit, canView])

  const loadClientes = useCallback(async () => {
    try {
      const response = await customerService.getAll({ limit: 1000 })
      if (response.success) {
        setClientes(response.data || [])
      }
    } catch (error) {
      console.error('Erro ao carregar clientes:', error)
    }
  }, [])

  const loadUsuarios = useCallback(async () => {
    try {
      const response = await userService.getAll({ limit: 1000 })
      if (response.success) {
        setUsuarios(response.data || [])
      }
    } catch (error) {
      console.error('Erro ao carregar usuários:', error)
    }
  }, [])

  const loadContasFinanceiras = useCallback(async () => {
    try {
      const response = await financialAccountService.getAll({ active: true, limit: 200 })
      if (response.success) {
        setContasFinanceiras(response.data || [])
      }
    } catch (error) {
      console.error('Erro ao carregar contas financeiras:', error)
    }
  }, [])

  const loadCategoriasReceita = useCallback(async () => {
    try {
      const response = await financialCategoryService.getAll({ type: 'revenue', active: true })
      if (response.success) {
        setCategoriasReceita(response.data || [])
      }
    } catch (error) {
      console.error('Erro ao carregar categorias de receita:', error)
    }
  }, [])

  useEffect(() => {
    loadContas()
    loadClientes()
    loadUsuarios()
    loadContasFinanceiras()
    loadCategoriasReceita()
  }, [loadContas, loadClientes, loadUsuarios, loadContasFinanceiras, loadCategoriasReceita])

  useEffect(() => {
    const search = location.state?.search || searchParams.get('search')
    const dashboardPreset = location.state?.dashboardPreset

    let hasStateToClear = false

    if (search) {
      hasStateToClear = hasStateToClear || Boolean(location.state?.search)
      setFilters((prev) => ({ ...prev, search: String(search) }))
    }

    if (dashboardPreset === 'next7days') {
      hasStateToClear = true
      setFilters((prev) => ({
        ...prev,
        status: ['pending', 'overdue'],
        dueDateFrom: '',
        dueDateTo: '',
        nextDays: 7,
        lastDays: null
      }))
    }

    if (dashboardPreset === 'dateRange') {
      const dueDateFrom = location.state?.dueDateFrom
      const dueDateTo = location.state?.dueDateTo
      if (dueDateFrom && dueDateTo) {
        hasStateToClear = true
        setFilters((prev) => ({
          ...prev,
          status: ['pending', 'overdue'],
          dueDateFrom,
          dueDateTo,
          nextDays: null,
          lastDays: null
        }))
      }
    }

    if (dashboardPreset === 'overdue') {
      hasStateToClear = true
      setFilters((prev) => ({
        ...prev,
        status: ['overdue'],
        dueDateFrom: '',
        dueDateTo: '',
        nextDays: null,
        lastDays: null
      }))
    }

    if (hasStateToClear) {
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, location.pathname, navigate, searchParams])

  const filtersKey = useMemo(() => JSON.stringify(filters), [filters])

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }))
  }, [filtersKey])

  // Fechar menu ao clicar fora
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openMenuId && !e.target.closest('.contas-receber-menu-button') && !e.target.closest('.contas-receber-dropdown-menu')) {
        setOpenMenuId(null)
        setMenuAnchor(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [openMenuId])

  useEffect(() => {
    if (!openMenuId) return undefined
    const close = () => {
      setOpenMenuId(null)
      setMenuAnchor(null)
    }
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => {
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [openMenuId])

  useEffect(() => {
    if (!workflowOpen || !workflowAccountId) return undefined
    let active = true
    setWorkflowLoading(true)
    setWorkflowChoice(null)
    cashStatementService.scanReceivables(workflowAccountId)
      .then((response) => {
        if (active) setWorkflowScan(response.data || { scanned: 0, matched: 0, items: [] })
      })
      .catch((error) => {
        if (!active) return
        setWorkflowScan(null)
        showToast(error.response?.data?.message || 'Erro ao varrer o extrato', 'error')
      })
      .finally(() => {
        if (active) setWorkflowLoading(false)
      })
    return () => { active = false }
  }, [workflowOpen, workflowAccountId, showToast])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErrors({})

    try {
      const dataToSend = {
        customerId: formData.personType === 'customer' ? formData.personId || null : null,
        userId: formData.personType === 'user' ? formData.personId || null : null,
        observation: formData.description || null, // Usar observation no backend
        amount: parseFloat(formData.amount) || 0,
        dueDate: formData.dueDate || null,
        status: formData.status || 'pending',
        categoryId: formData.categoryId || null,
        costCenterId: formData.costCenterId || null,
        financialAccountId: formData.financialAccountId || null,
        interestRate: parseFloat(formData.interestRate) || 0,
        fineRate: parseFloat(formData.fineRate) || 0,
        observations: formData.observations || null,
        applyToFuture: Boolean(editingConta && applyToFuture && futureCount > 0)
      }

      let response
      if (editingConta) {
        response = await accountReceivableService.update(editingConta.id, dataToSend)
      } else {
        response = await accountReceivableService.create(dataToSend)
      }

      if (response.success) {
        setModalOpen(false)
        resetForm()
        loadContas()
      } else {
        if (response.errors && Array.isArray(response.errors)) {
          const errorObj = {}
          response.errors.forEach(err => {
            errorObj[err.param || err.path] = err.msg || err.message
          })
          setErrors(errorObj)
        } else {
          setErrors({ submit: response.message || 'Erro ao salvar conta' })
        }
      }
    } catch (error) {
      console.error('Erro ao salvar conta:', error)
      const errorMessage = error.response?.data?.message || 'Erro ao salvar conta'
      const errorDetails = error.response?.data?.errors
      
      if (errorDetails && Array.isArray(errorDetails)) {
        const errorObj = {}
        errorDetails.forEach(err => {
          errorObj[err.param || err.path] = err.msg || err.message
        })
        setErrors(errorObj)
      } else {
        setErrors({ submit: errorMessage })
      }
    }
  }

  const closeBaixa = () => {
    setBaixaModalOpen(false)
    setContaParaBaixa(null)
    setSettlementChoice(null)
    setBaixaFormData({ paidAmount: '', paymentDate: toDateInputValue(new Date()), financialAccountId: '' })
  }

  const submitBaixa = async (settlementMode) => {
    if (contasFinanceiras.length === 0) return
    if (!baixaFormData.financialAccountId) {
      setErrors({ financialAccountId: 'Selecione a conta para dar baixa' })
      return
    }
    setErrors({})

    const remaining = receivableRemaining(contaParaBaixa)
    const payment = parseFloat(baixaFormData.paidAmount)
    if (!Number.isFinite(payment) || payment <= 0) {
      setErrors({ paidAmount: 'Informe o valor recebido' })
      return
    }
    if (payment > remaining + 0.009) {
      setErrors({ paidAmount: 'O valor recebido é maior que o saldo da conta' })
      return
    }
    if (payment + 0.009 < remaining && !settlementMode) {
      setSettlementChoice({
        payment,
        remaining,
        shortfall: Math.round((remaining - payment) * 100) / 100
      })
      return
    }

    try {
      const response = await accountReceivableService.markAsPaid(
        contaParaBaixa.id,
        payment,
        baixaFormData.paymentDate,
        baixaFormData.financialAccountId,
        settlementMode
      )

      if (response.success) {
        closeBaixa()
        loadContas()
        showToast(response.message || 'Baixa registrada', 'success')
      } else {
        setErrors({ submit: response.message || 'Erro ao dar baixa na conta' })
      }
    } catch (error) {
      const choice = error.response?.data
      if (choice?.code === 'SETTLEMENT_CHOICE') {
        setSettlementChoice({
          payment: choice.payment,
          remaining: choice.remaining,
          shortfall: choice.shortfall
        })
        return
      }
      console.error('Erro ao dar baixa:', error)
      setErrors({ submit: error.response?.data?.message || 'Erro ao dar baixa na conta' })
    }
  }

  const handleBaixa = (event) => {
    event.preventDefault()
    submitBaixa(null)
  }

  const handleSelectPerson = (person) => {
    setFormData((prev) => ({
      ...prev,
      personType: person.type,
      personId: String(person.id),
      personName: person.name
    }))
  }

  const handleEdit = (conta) => {
    const person = resolvePersonFields(conta)
    setEditingConta(conta)
    setApplyToFuture(false)
    setFutureCount(0)
    accountReceivableService.futureInstallments(conta.id)
      .then((response) => setFutureCount(response.data?.futureCount || 0))
      .catch(() => setFutureCount(0))
    setFormData({
      ...person,
      description: conta.description || '',
      amount: conta.amount || '',
      dueDate: toDateInputValue(conta.due_date),
      status: conta.status || 'pending',
      categoryId: conta.category_id || null,
      costCenterId: conta.cost_center_id || null,
      financialAccountId: conta.financial_account_id || null,
      interestRate: conta.interest_rate || 0,
      fineRate: conta.fine_rate || 0,
      observations: conta.observation || conta.observations || ''
    })
    setModalOpen(true)
  }

  const confirmDelete = async (conta, scope) => {
    try {
      const response = await accountReceivableService.delete(conta.id, scope)
      if (response.success) {
        setScopePrompt(null)
        loadContas()
        showToast(response.message || 'Conta removida', 'success')
      }
    } catch (error) {
      console.error('Erro ao excluir conta:', error)
      showToast(error.response?.data?.message || 'Erro ao excluir conta', 'error')
    }
  }

  const handleDelete = async (conta) => {
    let count = 0
    try {
      const response = await accountReceivableService.futureInstallments(conta.id)
      count = response.data?.futureCount || 0
    } catch (error) {
      count = 0
    }
    if (count > 0) {
      setScopePrompt({ conta, futureCount: count })
      return
    }
    if (window.confirm(`Deseja realmente excluir a conta "${conta.description || conta.id}"?`)) {
      confirmDelete(conta)
    }
  }

  const handleBaixaClick = (conta) => {
    setContaParaBaixa(conta)
    setSettlementChoice(null)
    setBaixaFormData({
      paidAmount: receivableRemaining(conta) || '',
      paymentDate: toDateInputValue(new Date()),
      financialAccountId: conta.financial_account_id ? String(conta.financial_account_id) : ''
    })
    setErrors({})
    setBaixaModalOpen(true)
  }

  const resetForm = () => {
    setFormData({
      personType: 'customer',
      personId: '',
      personName: '',
      description: '',
      amount: '',
      dueDate: '',
      status: 'pending',
      categoryId: null,
      costCenterId: null,
      financialAccountId: null,
      interestRate: 0,
      fineRate: 0,
      observations: ''
    })
    setEditingConta(null)
    setFutureCount(0)
    setApplyToFuture(false)
    setErrors({})
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0)
  }

  const formatDate = (dateString) => {
    return formatDateOnly(dateString)
  }

  const getLatestBoleto = (row) => {
    if (!row?.latest_boleto) return null
    if (typeof row.latest_boleto === 'string') {
      try {
        return JSON.parse(row.latest_boleto)
      } catch {
        return null
      }
    }
    return row.latest_boleto
  }

  const getBoletoStatusConfig = (status) => {
    switch (status) {
      case 'PAID':
        return { label: 'Boleto pago', className: 'paid' }
      case 'WAITING':
        return { label: 'Boleto emitido', className: 'waiting' }
      case 'DECLINED':
        return { label: 'Boleto negado', className: 'declined' }
      case 'EXPIRED':
        return { label: 'Boleto vencido', className: 'expired' }
      default:
        return { label: 'Boleto emitido', className: 'issued' }
    }
  }

  const renderBoletoBadge = (row) => {
    const boleto = getLatestBoleto(row)
    if (!boleto?.id) return null

    const config = getBoletoStatusConfig(boleto.status)
    return (
      <span
        className={`boleto-status-badge ${config.className}`}
        title={`${config.label}${boleto.due_date ? ` — vencimento ${formatDate(boleto.due_date)}` : ''}`}
      >
        <FileText size={12} />
        {config.label}
      </span>
    )
  }

  const hasActiveBoleto = (row) => {
    const boleto = getLatestBoleto(row)
    return boleto?.status === 'WAITING'
  }

  const getStatusBadge = (status, dueDate) => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const due = parseDateOnly(dueDate)
    const isOverdue = Boolean(due) && due < today && status !== 'paid'

    if (status === 'paid') {
      return <span className="status-badge paid">Pago</span>
    } else if (isOverdue || status === 'overdue') {
      return <span className="status-badge overdue">Vencido</span>
    } else if (status === 'cancelled') {
      return <span className="status-badge cancelled">Cancelado</span>
    } else {
      return <span className="status-badge pending">Pendente</span>
    }
  }

  const getOriginName = (row) => {
    if (row.reference_type === 'patient_charge') {
      return row.patient_record_number
        ? `Paciente ${row.patient_record_number}`
        : 'Paciente'
    }

    // Verificar se é de uma NF-e
    if (row.reference_type === 'NFE' || row.nfe_id || row.nfe_id_from_join) {
      const nfeNumber = row.nfe_number || row.nfe_id || row.nfe_id_from_join || row.reference_id
      return `NF-e ${nfeNumber}`
    }
    
    // Verificar se é de um cliente
    if (row.customer_id) {
      const cliente = clientes.find(c => String(c.id) === String(row.customer_id))
      return cliente ? `Cliente: ${cliente.name}` : (row.customer_name ? `Cliente: ${row.customer_name}` : '-')
    }
    
    // Verificar se é de um funcionário
    if (row.user_id) {
      const usuario = usuarios.find(u => u.id === row.user_id)
      return usuario ? `Funcionário: ${usuario.name}` : '-'
    }
    
    return '-'
  }

  const hasActiveFilters = useMemo(() => {
    const defaultStatus = ['pending', 'overdue']
    const statusChanged = JSON.stringify([...filters.status].sort()) !== JSON.stringify(defaultStatus.sort())
    const nextDaysChanged = filters.nextDays != null && filters.nextDays !== 7
    const lastDaysChanged = filters.lastDays !== null
    const periodChanged = Boolean(filters.dueDateFrom && filters.dueDateTo)
    return statusChanged ||
           filters.customerId !== '' ||
           filters.userId !== '' ||
           filters.originType !== '' ||
           nextDaysChanged ||
           lastDaysChanged ||
           periodChanged
  }, [filters])

  const hasDateRangeFilter = Boolean(filters.dueDateFrom && filters.dueDateTo)

  const resetFilters = () => {
    setFilters({
      search: '',
      status: ['pending', 'overdue'], // Resetar para padrão (sem pagos)
      customerId: '',
      userId: '',
      originType: '',
      dueDateFrom: '',
      dueDateTo: '',
      nextDays: 7,
      lastDays: null
    })
    setIsPeriodOpen(false)
  }

  const handlePeriodRangeChange = (start, end) => {
    let from = start
    let to = end
    if (from && to && from > to) {
      [from, to] = [to, from]
    }
    setFilters((prev) => ({
      ...prev,
      dueDateFrom: from,
      dueDateTo: to,
      nextDays: null,
      lastDays: null
    }))
  }

  const handleClearPeriodFilter = () => {
    setFilters((prev) => ({
      ...prev,
      dueDateFrom: '',
      dueDateTo: '',
      nextDays: 7,
      lastDays: null
    }))
  }

  const handleExportPdf = async () => {
    setGeneratingPDF(true)
    try {
      const response = await accountReceivableService.getAll({
        ...filters,
        page: 1,
        limit: 10000
      })

      if (!response.success || !response.data?.length) {
        showToast('Nenhuma conta encontrada com os filtros atuais', 'warning')
        return
      }

      setExportPdfAccounts(response.data)
      await new Promise((resolve) => setTimeout(resolve, 200))

      if (!pdfRef.current) {
        throw new Error('Falha ao montar documento PDF')
      }

      const customerSlug = response.data.length === 1 && response.data[0].customer_name
        ? response.data[0].customer_name.replace(/\s+/g, '_').slice(0, 30)
        : 'filtro'
      const filename = `Contas_a_Receber_${customerSlug}_${Date.now()}.pdf`

      await exportElementToPdf(pdfRef.current, filename)
      showToast(`${response.data.length} conta(s) exportada(s) em PDF`, 'success')
    } catch (error) {
      console.error('Erro ao exportar PDF:', error)
      showToast('Erro ao gerar PDF. Tente novamente.', 'error')
    } finally {
      setGeneratingPDF(false)
      setExportPdfAccounts([])
    }
  }

  const toggleStatusFilter = (statusValue) => {
    setFilters(prev => {
      const currentStatus = Array.isArray(prev.status) ? prev.status : [prev.status].filter(Boolean)
      const isSelected = currentStatus.includes(statusValue)
      
      let newStatus
      if (isSelected) {
        // Remover o status
        newStatus = currentStatus.filter(s => s !== statusValue)
      } else {
        // Adicionar o status
        newStatus = [...currentStatus, statusValue]
      }
      
      // Se nenhum status estiver selecionado, manter pelo menos um padrão
      if (newStatus.length === 0) {
        newStatus = ['pending']
      }
      
      return { ...prev, status: newStatus }
    })
  }

  const handleMenuToggle = (contaId, e) => {
    if (e) e.stopPropagation()
    if (openMenuId === contaId) {
      setOpenMenuId(null)
      setMenuAnchor(null)
      return
    }
    const rect = e.currentTarget.getBoundingClientRect()
    const openUp = rect.bottom + 260 > window.innerHeight
    setMenuAnchor({
      top: openUp ? rect.top - 8 : rect.bottom + 8,
      left: rect.right,
      openUp
    })
    setOpenMenuId(contaId)
  }

  const handleMenuAction = (action, conta) => {
    setOpenMenuId(null)
    setMenuAnchor(null)
    if (action === 'baixa') {
      if (!canMarkPaid) return
      handleBaixaClick(conta)
    } else if (action === 'edit') {
      if (!canUpdate) return
      handleEdit(conta)
    } else if (action === 'delete') {
      if (!canDelete) return
      handleDelete(conta)
    } else if (action === 'gerar-boleto') {
      if (!canBoleto) return
      handleGerarBoleto(conta)
    } else if (action === 'detalhes') {
      setContaDetalhes(conta)
      setDetalhesModalOpen(true)
    }
  }

  const getInstallmentInfo = (row) => {
    // Tentar extrair informações de parcela da descrição ou reference_id
    if (row.description && row.description.includes('Parcela')) {
      const match = row.description.match(/Parcela (\d+)\/(\d+)/)
      if (match) {
        return {
          current: parseInt(match[1]),
          total: parseInt(match[2])
        }
      }
    }
    return null
  }

  const handleGerarBoleto = (conta) => {
    setContaParaBoleto(conta)
    setBoletoModalOpen(true)
  }

  const handleConfirmarBoleto = async () => {
    if (!contaParaBoleto) return

    setBoletoLoading(true)
    try {
      const response = await boletoService.create(contaParaBoleto.id)
      if (response.success) {
        showToast('Boleto gerado com sucesso!', 'success')
        setBoletoModalOpen(false)
        setContaParaBoleto(null)
        // Recarregar contas para atualizar a lista
        loadContas()
      } else {
        showToast(response.message || 'Erro ao gerar boleto', 'error')
      }
    } catch (error) {
      console.error('Erro ao gerar boleto:', error)
      showToast(error.response?.data?.message || 'Erro ao gerar boleto', 'error')
    } finally {
      setBoletoLoading(false)
    }
  }

  const handleCopyId = async (code, accountId) => {
    if (!code) {
      showToast('Código não disponível', 'warning')
      return
    }
    
    try {
      await navigator.clipboard.writeText(code)
      setCopiedId(accountId)
      showToast(`Código ${code} copiado! Use para buscar no extrato.`, 'success')
      setTimeout(() => setCopiedId(null), 2000)
    } catch (error) {
      console.error('Erro ao copiar:', error)
      showToast('Erro ao copiar código', 'error')
    }
  }

  const openLinkReview = (account, transaction) => {
    setLinkReview({ account, transaction })
    setLinkSuggestions(null)
    setLinkSwapChoice(null)
  }

  const closeLinkReview = () => {
    setLinkReview(null)
    setLinkSuggestions(null)
    setLinkSwapChoice(null)
    setLinkSuggestionsLoading(false)
  }

  const searchEquivalentTransactions = async () => {
    if (!linkReview?.account?.financial_account_id) {
      showToast('Esta conta não tem conta financeira para buscar no extrato', 'warning')
      return
    }
    setLinkSuggestionsLoading(true)
    setLinkSwapChoice(null)
    try {
      const linkedAmount = Math.abs(parseFloat(linkReview.transaction.amount || 0))
      const response = await cashStatementService.suggestEquivalentTransactions({
        financialAccountId: linkReview.account.financial_account_id,
        amount: linkedAmount,
        excludeTransactionId: linkReview.transaction.transaction_id,
        nearDate: linkReview.transaction.transaction_date
          ? String(linkReview.transaction.transaction_date).slice(0, 10)
          : undefined
      })
      setLinkSuggestions(response.data || { exact: [], approximate: [] })
    } catch (error) {
      console.error('Erro ao buscar equivalentes:', error)
      showToast(error.response?.data?.message || 'Erro ao buscar o extrato', 'error')
    } finally {
      setLinkSuggestionsLoading(false)
    }
  }

  const replaceLinkedTransaction = async (candidate, settlementMode) => {
    if (!linkReview) return
    const { account, transaction } = linkReview
    const linkedAmount = Math.abs(parseFloat(transaction.amount || 0))
    const nextAmount = Math.abs(parseFloat(candidate.amount || 0))
    const balanceAfterSwap = receivableRemaining(account) + linkedAmount
    if (!settlementMode && nextAmount + 0.009 < balanceAfterSwap && Math.abs(nextAmount - linkedAmount) > 0.01) {
      setLinkSwapChoice({
        candidate,
        payment: nextAmount,
        remaining: balanceAfterSwap,
        shortfall: Math.round((balanceAfterSwap - nextAmount) * 100) / 100
      })
      return
    }
    try {
      await cashStatementService.unlinkReconciliation(
        transaction.transaction_id,
        account.id,
        'account_receivable'
      )
      await cashStatementService.linkTransaction(
        candidate.id,
        account.id,
        'account_receivable',
        settlementMode || 'partial'
      )
      showToast('Vínculo atualizado com o lançamento do extrato', 'success')
      closeLinkReview()
      loadContas()
    } catch (error) {
      console.error('Erro ao trocar vínculo:', error)
      showToast(error.response?.data?.message || 'Não foi possível trocar o vínculo', 'error')
      loadContas()
    }
  }

  const openWorkflow = (accountId) => {
    const fallback = contasFinanceiras[0]?.id
    setWorkflowAccountId(accountId ? String(accountId) : (fallback ? String(fallback) : ''))
    setWorkflowScan(null)
    setWorkflowChoice(null)
    setWorkflowOpen(true)
  }

  const closeWorkflow = () => {
    setWorkflowOpen(false)
    setWorkflowChoice(null)
    setWorkflowLinkingId(null)
  }

  const openWorkflowDetails = (match) => {
    if (match.due_date || match.observation) {
      setContaDetalhes(match)
      setDetalhesModalOpen(true)
      return
    }
    const existing = contas.find((item) => String(item.id) === String(match.id))
    setContaDetalhes(existing || {
      id: match.id,
      number: match.number,
      description: match.description,
      observation: match.description,
      status: match.status,
      due_date: match.dueDate,
      amount: match.amount,
      paid_amount: match.paidAmount,
      discount_amount: match.discountAmount,
      interest_amount: match.interestAmount,
      fine_amount: match.fineAmount,
      customer_name: match.customerName
    })
    setDetalhesModalOpen(true)
  }

  const linkWorkflowMatch = async (item, match, settlementMode) => {
    const payment = Math.abs(parseFloat(item.transaction.amount || 0))
    const remaining = parseFloat(match.remaining || 0)
    if (!settlementMode && payment + 0.009 < remaining && Math.abs(payment - remaining) > 0.01) {
      setWorkflowChoice({
        item,
        match,
        payment,
        remaining,
        shortfall: Math.round((remaining - payment) * 100) / 100
      })
      return
    }
    const linkingId = `${item.transaction.id}-${match.id}`
    setWorkflowLinkingId(linkingId)
    try {
      await cashStatementService.linkTransaction(
        item.transaction.id,
        match.id,
        'account_receivable',
        settlementMode || 'partial'
      )
      showToast('Lançamento conciliado', 'success')
      setWorkflowChoice(null)
      setWorkflowScan((current) => {
        if (!current) return current
        const items = current.items
          .filter((entry) => entry.transaction.id !== item.transaction.id)
          .map((entry) => ({
            ...entry,
            exact: entry.exact.filter((candidate) => candidate.id !== match.id),
            approximate: entry.approximate.filter((candidate) => candidate.id !== match.id)
          }))
          .filter((entry) => entry.exact.length || entry.approximate.length)
        return { ...current, matched: items.length, items }
      })
      loadContas()
    } catch (error) {
      console.error('Erro ao conciliar lançamento:', error)
      showToast(error.response?.data?.message || 'Não foi possível conciliar', 'error')
    } finally {
      setWorkflowLinkingId(null)
    }
  }

  const confirmUnlinkReconciliation = async () => {
    if (!linkReview) return
    const { account, transaction } = linkReview
    try {
      const response = await cashStatementService.unlinkReconciliation(
        transaction.transaction_id,
        account.id,
        'account_receivable'
      )
      if (response && response.success) {
        showToast('Vínculo removido. O saldo da conta foi recalculado.', 'success')
        closeLinkReview()
        loadContas()
      } else {
        showToast(response?.message || 'Erro ao desvincular conta a receber', 'error')
      }
    } catch (error) {
      console.error('Erro ao desvincular conta a receber:', error)
      showToast(error.response?.data?.message || 'Erro ao desvincular conta a receber', 'error')
    }
  }

  const columns = [
    {
      key: 'customer',
      label: 'Cliente',
      render: (value, row) => {
        const personName = row.customer_name || row.patient_name
        if (personName) {
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <User size={16} style={{ color: 'var(--text-tertiary, #6b7280)' }} />
              <span>{personName}</span>
            </div>
          )
        }
        if (row.user_name) {
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <User size={16} style={{ color: 'var(--text-tertiary, #6b7280)' }} />
              <span>{row.user_name} (Funcionário)</span>
            </div>
          )
        }
        return <span style={{ color: 'var(--text-tertiary, #6b7280)', fontStyle: 'italic' }}>-</span>
      }
    },
    {
      key: 'origin',
      label: 'Origem',
      render: (value, row) => getOriginName(row)
    },
    {
      key: 'amount',
      label: 'Valor',
      render: (value, row) => {
        // Verificar se há transações vinculadas
        const linkedTransactions = row.linked_transactions || []
        const hasLinks = linkedTransactions.length > 0
        
        // Se há transações vinculadas, mostrar valor vinculado
        let displayValue = formatCurrency(value)
        
        if (hasLinks) {
          const totalLinked = linkedTransactions.reduce((sum, transaction) => {
            return sum + (parseFloat(transaction.amount) || 0)
          }, 0)
          
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              <span>{displayValue}</span>
              <span style={{ 
                fontSize: '0.7rem', 
                color: 'var(--text-tertiary, #6b7280)',
                fontStyle: 'italic'
              }}>
                ({linkedTransactions.length} vínculo{linkedTransactions.length > 1 ? 's' : ''}: {formatCurrency(totalLinked)})
              </span>
              {linkedTransactions.map((transaction, idx) => (
                <button
                  key={idx}
                  onClick={(e) => {
                    e.stopPropagation()
                    openLinkReview(row, transaction)
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    color: 'var(--color-error, #ef4444)',
                    transition: 'color 0.2s',
                    marginLeft: '0.25rem'
                  }}
                  title={`Ver vínculo: ${transaction.description || 'Transação'} (${formatCurrency(transaction.amount || 0)})`}
                >
                  <Unlink size={16} />
                </button>
              ))}
            </div>
          )
        }
        
        return <span>{displayValue}</span>
      }
    },
    {
      key: 'due_date',
      label: 'Vencimento',
      render: (value) => formatDate(value)
    },
    {
      key: 'status',
      label: 'Status',
      render: (value, row) => (
        <div className="contas-receber-status-cell">
          {getStatusBadge(value, row.due_date)}
          {renderBoletoBadge(row)}
        </div>
      )
    },
    {
      key: 'id',
      label: 'CÓDIGO',
      render: (value, row) => {
        // Usar o ID da conta a receber como código único para busca
        const accountId = value || row.id
        if (!accountId) {
          return <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>N/A</span>
        }
        const isCopied = copiedId === row.id
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <code style={{ 
              fontSize: '0.75rem', 
              background: 'var(--bg-secondary, #f3f4f6)', 
              padding: '0.25rem 0.5rem', 
              borderRadius: '4px',
              fontFamily: 'monospace',
              color: 'var(--text-primary, #1f2937)'
            }}>
              {accountId}
            </code>
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleCopyId(accountId.toString(), row.id)
              }}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '0.25rem',
                display: 'flex',
                alignItems: 'center',
                color: isCopied ? 'var(--color-success, #10b981)' : 'var(--text-tertiary, #6b7280)',
                transition: 'color 0.2s'
              }}
              title="Copiar código da conta a receber para buscar no extrato"
            >
              {isCopied ? <Check size={16} /> : <Copy size={16} />}
            </button>
          </div>
        )
      }
    },
    {
      key: 'actions',
      label: '',
      render: (value, row) => (
        <div style={{ position: 'relative', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', alignItems: 'center' }}>
          <button
            className="contas-receber-menu-button"
            onClick={(e) => {
              e.stopPropagation()
              openWorkflow(row.financial_account_id)
            }}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '0.25rem',
              display: 'flex',
              alignItems: 'center',
              color: 'var(--text-tertiary, #6b7280)',
              transition: 'color 0.2s'
            }}
            title="Buscar no extrato para vincular"
          >
            <Workflow size={16} />
          </button>
          <button
            className="contas-receber-menu-button"
            onClick={(e) => handleMenuToggle(row.id, e)}
            title="Ações da conta"
          >
            <ChartNoAxesGantt size={16} />
          </button>
          {openMenuId === row.id && menuAnchor && createPortal(
            <div
              className={`contas-receber-dropdown-menu contas-receber-dropdown-menu--portal${menuAnchor.openUp ? ' is-up' : ''}`}
              style={{ top: menuAnchor.top, left: menuAnchor.left }}
            >
              <button onClick={() => handleMenuAction('detalhes', row)}>
                <FileSearch size={16} />
                Visualizar
              </button>
              {row.status !== 'paid' && row.status !== 'cancelled' && (
                <>
                  {canMarkPaid && (
                    <button onClick={() => handleMenuAction('baixa', row)}>
                      <DollarSign size={16} />
                      Dar Baixa
                    </button>
                  )}
                  {canBoleto && (
                    hasActiveBoleto(row) ? (
                      <button
                        type="button"
                        className="boleto-menu-disabled"
                        onClick={() => handleMenuAction('gerar-boleto', row)}
                      >
                        <FileText size={16} />
                        Boleto já emitido
                      </button>
                    ) : (
                      <button onClick={() => handleMenuAction('gerar-boleto', row)}>
                        <FileText size={16} />
                        Gerar Boleto
                      </button>
                    )
                  )}
                </>
              )}
              {canUpdate && (
                <button onClick={() => handleMenuAction('edit', row)}>
                  <Edit size={16} />
                  Editar
                </button>
              )}
              {canDelete && (
                <button onClick={() => handleMenuAction('delete', row)} className="danger">
                  <Trash2 size={16} />
                  Excluir
                </button>
              )}
            </div>,
            document.body
          )}
        </div>
      )
    }
  ]

  const actions = []

  const handleImportFileSelect = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      setImportFile(file)
    }
  }

  const handleImportDrop = (e) => {
    e.preventDefault()
    const file = e.dataTransfer.files?.[0]
    if (file) {
      setImportFile(file)
    }
  }

  const handleImportPreview = async () => {
    if (!importFile) return
    setImportLoading(true)
    try {
      const response = await accountReceivableService.previewImport(importFile)
      if (response.success && response.data?.success !== false) {
        setImportPreview(response.data)
        setImportStep('preview')
      } else {
        showToast(response.data?.error || response.message || 'Erro ao processar arquivo', 'error')
      }
    } catch (error) {
      console.error('Erro no preview de importação:', error)
      showToast(error.response?.data?.message || error.message || 'Erro ao processar arquivo', 'error')
    } finally {
      setImportLoading(false)
    }
  }

  const handleImportExecute = async () => {
    if (!importFile) return
    setImportLoading(true)
    try {
      const response = await accountReceivableService.executeImport(importFile)
      if (response.success) {
        setImportResult(response.data)
        setImportStep('result')
        loadContas()
        showToast(`${response.data.created} contas importadas com sucesso`, 'success')
      } else {
        showToast(response.data?.error || 'Erro ao importar', 'error')
      }
    } catch (error) {
      showToast(error.response?.data?.message || 'Erro ao importar', 'error')
    } finally {
      setImportLoading(false)
    }
  }

  const handleImportClose = () => {
    setImportModalOpen(false)
    setImportStep('upload')
    setImportFile(null)
    setImportPreview(null)
    setImportResult(null)
    if (importFileInputRef.current) {
      importFileInputRef.current.value = ''
    }
  }

  if (!canView) {
    return (
      <div className="contas-receber-page">
        <div className="page-header">
          <h1>Contas a Receber</h1>
          <p>Você não tem permissão para visualizar contas a receber.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="contas-receber-page">
      <div className="page-header">
        <div>
          <h1>Contas a Receber</h1>
          <p>Gerencie suas contas a receber</p>
        </div>
        {canCreate && (
          <button
            className="btn-primary"
            onClick={() => {
              resetForm()
              setModalOpen(true)
            }}
          >
            <Plus size={18} />
            Nova Conta
          </button>
        )}
      </div>

      {/* Main Table Container */}
      <div className="contas-receber-table-container">
        <div className="contas-receber-table-header">
          <div className="contas-receber-search-wrapper">
            <Search className="contas-receber-search-icon" size={18} />
            <input 
              type="text" 
              placeholder="Pesquisar contas a receber..." 
              className="contas-receber-search-input"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            />
          </div>
          <div className="contas-receber-table-actions">
            <button
              type="button"
              className="contas-receber-export-button"
              onClick={() => openWorkflow('')}
              title="Varrer o extrato e conciliar"
            >
              <Workflow size={14} /> Workflow
            </button>
            <button
              onClick={() => setIsFilterOpen(true)}
              className={`contas-receber-filter-button ${hasActiveFilters ? 'active' : ''}`}
            >
              <Filter size={14} /> Filtros
              {hasActiveFilters && (
                <span className="contas-receber-filter-badge" />
              )}
            </button>
            <button
              ref={periodBtnRef}
              type="button"
              onClick={() => setIsPeriodOpen((prev) => !prev)}
              className={`contas-receber-filter-button ${hasDateRangeFilter ? 'active' : ''}`}
              title="Filtrar por período de vencimento"
            >
              <CalendarIcon size={14} /> Período
              {hasDateRangeFilter && (
                <span className="contas-receber-filter-badge" />
              )}
            </button>
            <CalendarRangePicker
              open={isPeriodOpen}
              onOpenChange={setIsPeriodOpen}
              anchorRef={periodBtnRef}
              startValue={filters.dueDateFrom}
              endValue={filters.dueDateTo}
              onRangeChange={handlePeriodRangeChange}
              onClear={handleClearPeriodFilter}
              title="Vencimento"
              subtitle="Início e Fim"
            />
            {canCreate && (
              <button className="contas-receber-export-button" onClick={() => setImportModalOpen(true)}>
                <Upload size={14} /> Importar
              </button>
            )}
            <button
              type="button"
              className="contas-receber-export-button"
              onClick={handleExportPdf}
              disabled={generatingPDF || loading}
            >
              <Download size={14} /> Exportar
            </button>
          </div>
        </div>

        <div className="contas-receber-table-wrapper">
          <DataTable
            columns={columns}
            data={contas}
            loading={loading}
            actions={actions}
            emptyMessage="Nenhuma conta a receber encontrada"
            pagination={pagination.total > 0 ? {
              from: (pagination.page - 1) * pagination.limit + 1,
              to: Math.min(pagination.page * pagination.limit, pagination.total),
              total: pagination.total,
              current: pagination.page,
              totalPages: pagination.totalPages,
              hasPrev: pagination.page > 1,
              hasNext: pagination.page < pagination.totalPages,
              onPrev: () => setPagination((prev) => ({ ...prev, page: prev.page - 1 })),
              onNext: () => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))
            } : null}
          />
        </div>
      </div>

      {/* Side Panel: Filtros Avançados */}
      {isFilterOpen && (
        <>
          <div className="contas-receber-filter-overlay" onClick={() => setIsFilterOpen(false)} />
          <aside className="contas-receber-filter-sidebar contas-receber-scrollbar">
            <div className="contas-receber-filter-header">
              <div className="contas-receber-filter-header-content">
                <div className="contas-receber-filter-header-icon">
                  <Filter size={24} />
                </div>
                <div>
                  <h3 className="contas-receber-filter-header-title">
                    Filtros
                  </h3>
                  <p className="contas-receber-filter-header-subtitle">
                    Refinar resultados
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setIsFilterOpen(false)} 
                className="contas-receber-filter-close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="contas-receber-filter-content">
              {/* Status */}
              <div className="contas-receber-filter-section">
                <h4 className="contas-receber-filter-section-title">
                  Status (Selecione múltiplos)
                </h4>
                <div className="contas-receber-filter-options">
                  {[
                    { id: 'pending', label: 'Pendente', icon: AlertCircle },
                    { id: 'overdue', label: 'Vencido', icon: AlertCircle },
                    { id: 'paid', label: 'Pago', icon: CheckCircle2 },
                    { id: 'cancelled', label: 'Cancelado', icon: X },
                  ].map(opt => {
                    const currentStatus = Array.isArray(filters.status) ? filters.status : [filters.status].filter(Boolean)
                    const isSelected = currentStatus.includes(opt.id)
                    return (
                      <button
                        key={opt.id}
                        onClick={() => toggleStatusFilter(opt.id)}
                        className={`contas-receber-filter-option ${isSelected ? 'selected' : ''}`}
                      >
                        <div className="contas-receber-filter-option-content">
                          <opt.icon 
                            size={18} 
                            className="contas-receber-filter-option-icon"
                          />
                          <span className="contas-receber-filter-option-label">
                            {opt.label}
                          </span>
                        </div>
                        {isSelected && (
                          <Check size={16} className="contas-receber-filter-option-check" />
                        )}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Origem */}
              <div className="contas-receber-filter-section">
                <h4 className="contas-receber-filter-section-title">
                  Origem
                </h4>
                <div className="contas-receber-filter-options">
                  {[
                    { id: '', label: 'Todas as Origens', icon: Layers },
                    { id: 'customer', label: 'Cliente', icon: Sparkles },
                    { id: 'user', label: 'Funcionário', icon: Sparkles },
                  ].map(opt => (
                    <button
                      key={opt.id}
                      onClick={() => {
                        const originType = opt.id
                        setFilters({ 
                          ...filters, 
                          originType,
                          customerId: originType === 'customer' ? filters.customerId : '',
                          userId: originType === 'user' ? filters.userId : ''
                        })
                      }}
                      className={`contas-receber-filter-option ${filters.originType === opt.id ? 'selected' : ''}`}
                    >
                      <div className="contas-receber-filter-option-content">
                        <opt.icon 
                          size={18} 
                          className="contas-receber-filter-option-icon"
                        />
                        <span className="contas-receber-filter-option-label">
                          {opt.label}
                        </span>
                      </div>
                      {filters.originType === opt.id && (
                        <Check size={16} className="contas-receber-filter-option-check" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Filtro específico por origem */}
              {filters.originType === 'customer' && (
                <div className="contas-receber-filter-section">
                  <FilterLeftPicker
                    label="Cliente"
                    title="Cliente"
                    value={filters.customerId}
                    options={customerFilterOptions}
                    onChange={(value) => setFilters({ ...filters, customerId: value })}
                    emptyOptionLabel="Todos os clientes"
                    searchable
                    searchPlaceholder="Buscar cliente..."
                    openOnHover
                  />
                </div>
              )}

              {filters.originType === 'user' && (
                <div className="contas-receber-filter-section">
                  <FilterLeftPicker
                    label="Funcionário"
                    title="Funcionário"
                    value={filters.userId}
                    options={userFilterOptions}
                    onChange={(value) => setFilters({ ...filters, userId: value })}
                    emptyOptionLabel="Todos os funcionários"
                    searchable
                    searchPlaceholder="Buscar funcionário..."
                    openOnHover
                  />
                </div>
              )}

              {/* Vencimento - Próximos X dias */}
              <div className="contas-receber-filter-section">
                <h4 className="contas-receber-filter-section-title">
                  <CalendarIcon size={14} style={{ display: 'inline', marginRight: '0.5rem' }} />
                  Próximos Dias
                </h4>
                <div className="contas-receber-filter-options">
                  {[
                    { id: 7, label: 'Próximos 7 dias' },
                    { id: 30, label: 'Próximos 30 dias' },
                    { id: 60, label: 'Próximos 60 dias' },
                    { id: 90, label: 'Próximos 90 dias' },
                  ].map(opt => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setFilters({
                        ...filters,
                        nextDays: opt.id,
                        lastDays: null,
                        dueDateFrom: '',
                        dueDateTo: ''
                      })}
                      className={`contas-receber-filter-option ${!hasDateRangeFilter && filters.nextDays === opt.id ? 'selected' : ''}`}
                    >
                      <div className="contas-receber-filter-option-content">
                        <Clock size={18} className="contas-receber-filter-option-icon" />
                        <span className="contas-receber-filter-option-label">
                          {opt.label}
                        </span>
                      </div>
                      {!hasDateRangeFilter && filters.nextDays === opt.id && (
                        <Check size={16} className="contas-receber-filter-option-check" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Vencimento - Últimos X dias */}
              <div className="contas-receber-filter-section">
                <h4 className="contas-receber-filter-section-title">
                  <Clock size={14} style={{ display: 'inline', marginRight: '0.5rem' }} />
                  Últimos Dias
                </h4>
                <div className="contas-receber-filter-options">
                  {[
                    { id: null, label: 'Desativado' },
                    { id: 30, label: 'Últimos 30 dias' },
                    { id: 60, label: 'Últimos 60 dias' },
                    { id: 90, label: 'Últimos 90 dias' },
                  ].map(opt => (
                    <button
                      key={opt.id || 'none'}
                      type="button"
                      onClick={() => setFilters({
                        ...filters,
                        lastDays: opt.id,
                        nextDays: opt.id === null ? 7 : null,
                        dueDateFrom: '',
                        dueDateTo: ''
                      })}
                      className={`contas-receber-filter-option ${!hasDateRangeFilter && filters.lastDays === opt.id ? 'selected' : ''}`}
                    >
                      <div className="contas-receber-filter-option-content">
                        <Clock size={18} className="contas-receber-filter-option-icon" />
                        <span className="contas-receber-filter-option-label">
                          {opt.label}
                        </span>
                      </div>
                      {!hasDateRangeFilter && filters.lastDays === opt.id && (
                        <Check size={16} className="contas-receber-filter-option-check" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

            </div>

            <div className="contas-receber-filter-footer">
              <button 
                onClick={resetFilters}
                className="contas-receber-filter-reset"
              >
                <Trash2 size={14} /> Limpar Filtros
              </button>
              <button 
                onClick={() => setIsFilterOpen(false)}
                className="contas-receber-filter-apply"
              >
                Aplicar Filtros
              </button>
            </div>
          </aside>
        </>
      )}

      {/* Modal de criação/edição */}
      <Modal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false)
          resetForm()
        }}
        title={editingConta ? 'Editar Conta a Receber' : 'Nova Conta a Receber'}
        size="medium"
        footer={
          <>
            <button
              className="btn-secondary"
              onClick={() => {
                setModalOpen(false)
                resetForm()
              }}
            >
              Cancelar
            </button>
            <button
              className="btn-primary"
              onClick={handleSubmit}
            >
              {editingConta ? 'Salvar' : 'Criar'}
            </button>
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          <div className="contas-receber-edit-person-field">
            <label className="contas-receber-edit-person-label">
              {PERSON_TYPE_LABELS[formData.personType] || 'Destinatário'}
            </label>
            <button
              type="button"
              className="contas-receber-edit-person-picker"
              onClick={() => setShowPersonModal(true)}
            >
              <Search size={16} className="contas-receber-edit-person-picker-icon" />
              <span className={formData.personName ? '' : 'contas-receber-edit-person-placeholder'}>
                {formData.personName || 'Pesquisar por nome, CPF/CNPJ ou e-mail...'}
              </span>
            </button>
            {(errors.personId || errors.customerId) && (
              <span className="contas-receber-edit-person-error">
                {errors.personId || errors.customerId}
              </span>
            )}
          </div>

          <FormField
            label="Descrição"
            name="description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            error={errors.description}
          />

          <div className="form-row">
            <FormField
              label="Valor"
              name="amount"
              type="number"
              step="0.01"
              min="0.01"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              error={errors.amount}
              required
            />

            <div className="contas-receber-date-field">
              <label>Data de Vencimento</label>
              <CalendarDatePicker
                value={formData.dueDate}
                onChange={(value) => setFormData({ ...formData, dueDate: value })}
                title="Vencimento"
                placeholder="Selecionar data"
              />
              {errors.dueDate && <span className="form-error-message">{errors.dueDate}</span>}
            </div>
          </div>

          <div className="form-row">
            <div>
              <label>Status</label>
              <FieldDropdown
                value={formData.status}
                options={RECEIVABLE_STATUS_OPTIONS}
                onChange={(value) => setFormData({ ...formData, status: value })}
                ariaLabel="Status"
              />
            </div>

            <div>
              <label>Categoria de receita</label>
              <FieldDropdown
                value={formData.categoryId ? String(formData.categoryId) : ''}
                searchable
                ariaLabel="Categoria de receita"
                onChange={(value) => setFormData({ ...formData, categoryId: value || null })}
                options={[
                  { value: '', label: 'Selecione a categoria' },
                  ...categoriasReceita.map((categoria) => ({
                    value: String(categoria.id),
                    label: categoria.parent_name
                      ? `${categoria.parent_name} · ${categoria.name}`
                      : categoria.name
                  }))
                ]}
              />
            </div>

            <div>
              <label>Conta Financeira</label>
              <FieldDropdown
                value={formData.financialAccountId ? String(formData.financialAccountId) : ''}
                searchable
                ariaLabel="Conta financeira"
                onChange={(value) => setFormData({ ...formData, financialAccountId: value || null })}
                options={[
                  { value: '', label: 'Selecione uma conta' },
                  ...contasFinanceiras.map((conta) => ({
                    value: String(conta.id),
                    label: `${conta.name}${conta.code ? ` — ${conta.code}` : ''}`
                  }))
                ]}
              />
            </div>
          </div>

          {editingConta && futureCount > 0 && (
            <label className="contas-receber-future-option">
              <input
                type="checkbox"
                checked={applyToFuture}
                onChange={(event) => setApplyToFuture(event.target.checked)}
              />
              Aplicar também às {futureCount} parcelas futuras
            </label>
          )}

          <div className="form-row">
            <FormField
              label="Taxa de Juros (%)"
              name="interestRate"
              type="number"
              step="0.01"
              min="0"
              value={formData.interestRate}
              onChange={(e) => setFormData({ ...formData, interestRate: e.target.value })}
              error={errors.interestRate}
            />

            <FormField
              label="Taxa de Multa (%)"
              name="fineRate"
              type="number"
              step="0.01"
              min="0"
              value={formData.fineRate}
              onChange={(e) => setFormData({ ...formData, fineRate: e.target.value })}
              error={errors.fineRate}
            />
          </div>

          <FormField
            label="Observações"
            name="observations"
            type="textarea"
            value={formData.observations}
            onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
            error={errors.observations}
          />

          {errors.submit && (
            <div className="form-error-message">{errors.submit}</div>
          )}
        </form>
      </Modal>

      {/* Modal de baixa */}
      <Modal
        isOpen={baixaModalOpen}
        onClose={closeBaixa}
        title={settlementChoice ? 'Divergência de valor' : 'Dar Baixa na Conta'}
        size={settlementChoice ? 'medium' : 'small'}
        footer={
          settlementChoice ? (
            <button className="btn-secondary" onClick={() => setSettlementChoice(null)}>
              Voltar
            </button>
          ) : (
          <>
            <button className="btn-secondary" onClick={closeBaixa}>
              Cancelar
            </button>
            <button
              className="btn-primary"
              onClick={handleBaixa}
              disabled={contasFinanceiras.length === 0}
            >
              Confirmar Baixa
            </button>
          </>
          )
        }
      >
        {contaParaBaixa && settlementChoice && (
          <div className="receber-settlement">
            <p>O valor informado é menor que o saldo. Escolha se o restante fica em aberto ou se a conta será quitada com abatimento.</p>
            <div className="receber-settlement-details">
              <div><span>Saldo da conta</span><strong>{formatCurrency(settlementChoice.remaining)}</strong></div>
              <div><span>Valor recebido</span><strong>{formatCurrency(settlementChoice.payment)}</strong></div>
              <div><span>Diferença</span><strong>{formatCurrency(settlementChoice.shortfall)}</strong></div>
            </div>
            <div className="receber-settlement-options">
              <div>
                <h4>Deixar em aberto</h4>
                <p>Registra {formatCurrency(settlementChoice.payment)} e mantém {formatCurrency(settlementChoice.shortfall)} pendente.</p>
                <button type="button" className="btn-primary" onClick={() => submitBaixa('partial')}>
                  Confirmar pagamento parcial
                </button>
              </div>
              <div>
                <h4>Quitar com abatimento</h4>
                <p>Encerra a conta e lança abatimento de {formatCurrency(settlementChoice.shortfall)}.</p>
                <button type="button" className="btn-primary" onClick={() => submitBaixa('full_with_adjustment')}>
                  Quitar como pagamento final
                </button>
              </div>
            </div>
            {errors.submit && <div className="form-error-message">{errors.submit}</div>}
          </div>
        )}
        {contaParaBaixa && !settlementChoice && (
          <form onSubmit={handleBaixa}>
            <div className="baixa-info">
              <p><strong>Descrição:</strong> {contaParaBaixa.description || '-'}</p>
              <p><strong>Valor original:</strong> {formatCurrency(contaParaBaixa.amount)}</p>
              <p><strong>Saldo em aberto:</strong> {formatCurrency(receivableRemaining(contaParaBaixa))}</p>
              {contaParaBaixa.due_date && (
                <p><strong>Vencimento:</strong> {formatDate(contaParaBaixa.due_date)}</p>
              )}
            </div>

            {contasFinanceiras.length === 0 ? (
              <div className="baixa-account-empty">
                <p>Nenhuma conta financeira cadastrada. A baixa precisa de uma conta relacionada.</p>
                <Link to="/erp/financeiro/contas">Ir para Contas</Link>
              </div>
            ) : (
              <div>
                <label>Conta</label>
                <FieldDropdown
                  value={baixaFormData.financialAccountId}
                  searchable
                  ariaLabel="Conta da baixa"
                  onChange={(value) => setBaixaFormData({ ...baixaFormData, financialAccountId: value })}
                  options={[
                    { value: '', label: 'Selecione a conta' },
                    ...contasFinanceiras.map((conta) => ({
                      value: String(conta.id),
                      label: `${conta.name}${conta.code ? ` — ${conta.code}` : ''}`
                    }))
                  ]}
                />
                {errors.financialAccountId && <span className="form-error-message">{errors.financialAccountId}</span>}
              </div>
            )}

            <FormField
              label="Valor recebido"
              name="paidAmount"
              type="number"
              step="0.01"
              min="0.01"
              value={baixaFormData.paidAmount}
              onChange={(e) => setBaixaFormData({ ...baixaFormData, paidAmount: e.target.value })}
              error={errors.paidAmount}
              required
            />

            <div className="contas-receber-date-field">
              <label>Data de pagamento</label>
              <CalendarDatePicker
                value={baixaFormData.paymentDate}
                onChange={(value) => setBaixaFormData({ ...baixaFormData, paymentDate: value })}
                title="Pagamento"
                placeholder="Selecionar data"
              />
            </div>

            {errors.submit && (
              <div className="form-error-message">{errors.submit}</div>
            )}
          </form>
        )}
      </Modal>

      <Modal
        isOpen={Boolean(scopePrompt)}
        onClose={() => setScopePrompt(null)}
        title="Excluir parcelas"
        size="small"
        footer={
          <button className="btn-secondary" onClick={() => setScopePrompt(null)}>Cancelar</button>
        }
      >
        {scopePrompt && (
          <div className="receber-settlement">
            <p>
              Esta conta faz parte de uma série. Excluir só ela ou também as {scopePrompt.futureCount} parcelas futuras ainda em aberto?
            </p>
            <div className="receber-settlement-options">
              <div>
                <h4>Só esta</h4>
                <p>As próximas parcelas permanecem.</p>
                <button type="button" className="btn-secondary" onClick={() => confirmDelete(scopePrompt.conta)}>
                  Excluir somente esta
                </button>
              </div>
              <div>
                <h4>Esta e as futuras</h4>
                <p>Remove esta conta e as parcelas seguintes que ainda não tiveram recebimento.</p>
                <button type="button" className="btn-primary" onClick={() => confirmDelete(scopePrompt.conta, 'future')}>
                  Excluir esta e as futuras
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de confirmação de boleto */}
      <Modal
        isOpen={boletoModalOpen}
        onClose={() => {
          setBoletoModalOpen(false)
          setContaParaBoleto(null)
        }}
        title={hasActiveBoleto(contaParaBoleto) ? 'Boleto já emitido' : 'Gerar Boleto'}
        size="medium"
        footer={
          <>
            <button
              className="btn-secondary"
              onClick={() => {
                setBoletoModalOpen(false)
                setContaParaBoleto(null)
              }}
              disabled={boletoLoading}
            >
              {hasActiveBoleto(contaParaBoleto) ? 'Fechar' : 'Cancelar'}
            </button>
            {!hasActiveBoleto(contaParaBoleto) && (
              <button
                className="btn-primary"
                onClick={handleConfirmarBoleto}
                disabled={boletoLoading}
              >
                {boletoLoading ? 'Gerando...' : 'Confirmar e Gerar Boleto'}
              </button>
            )}
          </>
        }
      >
        {contaParaBoleto && (
          <div className="boleto-confirmation">
            {getLatestBoleto(contaParaBoleto) && (
              <div className={`boleto-alert ${hasActiveBoleto(contaParaBoleto) ? 'boleto-alert-warning' : 'boleto-alert-info'}`}>
                <AlertCircle size={20} />
                <div>
                  {hasActiveBoleto(contaParaBoleto) ? (
                    <>
                      <strong>Boleto já emitido para esta conta</strong>
                      <p>
                        Já existe um boleto ativo aguardando pagamento
                        {getLatestBoleto(contaParaBoleto).due_date && (
                          <> com vencimento em <strong>{formatDate(getLatestBoleto(contaParaBoleto).due_date)}</strong></>
                        )}.
                        Não é possível gerar outro boleto enquanto este estiver pendente.
                      </p>
                    </>
                  ) : (
                    <>
                      <strong>Esta conta já teve boleto emitido anteriormente</strong>
                      <p>
                        Status do último boleto: <strong>{getBoletoStatusConfig(getLatestBoleto(contaParaBoleto).status).label}</strong>.
                        Você pode gerar um novo boleto se necessário.
                      </p>
                    </>
                  )}
                </div>
              </div>
            )}
            <div className="boleto-info">
              <h3>Dados da Conta a Receber</h3>
              <p><strong>Descrição:</strong> {contaParaBoleto.description || contaParaBoleto.observation || '-'}</p>
              <p><strong>Cliente:</strong> {contaParaBoleto.customer_name || contaParaBoleto.user_name || '-'}</p>
              <p><strong>Valor:</strong> {formatCurrency(contaParaBoleto.amount)}</p>
              {contaParaBoleto.due_date && (
                <p><strong>Vencimento:</strong> {formatDate(contaParaBoleto.due_date)}</p>
              )}
            </div>

            {!hasActiveBoleto(contaParaBoleto) && (
              <>
                <div className="boleto-encargos-info">
                  <h3>Informações sobre Encargos</h3>
                  <div className="encargos-list">
                    <div className="encargo-item">
                      <AlertCircle size={18} />
                      <div>
                        <strong>Juros:</strong> 2% ao mês após o vencimento
                      </div>
                    </div>
                    <div className="encargo-item">
                      <AlertCircle size={18} />
                      <div>
                        <strong>Multa:</strong> 3% após 5 dias de atraso
                      </div>
                    </div>
                    <div className="encargo-item">
                      <AlertCircle size={18} />
                      <div>
                        <strong>Protesto:</strong> Será realizado após 15 dias de vencimento (feito diretamente com o cartório)
                      </div>
                    </div>
                  </div>
                </div>

                <div className="boleto-warning">
                  <p>O boleto será gerado via PagSeguro e estará disponível para download após a criação.</p>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>

      <Modal
        isOpen={workflowOpen}
        onClose={closeWorkflow}
        title="Conciliar no extrato"
        size="large"
        footer={
          <button className="btn-secondary" onClick={closeWorkflow}>
            Fechar
          </button>
        }
      >
        <div className="receber-scan">
          <label className="receber-scan-account">
            <span>Conta do extrato</span>
            <FieldDropdown
              searchable
              ariaLabel="Conta do extrato"
              value={workflowAccountId}
              onChange={(value) => setWorkflowAccountId(value ? String(value) : '')}
              options={contasFinanceiras.map((conta) => ({
                value: String(conta.id),
                label: `${conta.name}${conta.code ? ` — ${conta.code}` : ''}`
              }))}
            />
          </label>

          {workflowLoading && <p className="receber-scan-status">Varrendo o extrato…</p>}

          {workflowScan && !workflowLoading && (
            <p className="receber-scan-status">
              {workflowScan.scanned} lançamentos varridos · {workflowScan.matched} com sugestão
            </p>
          )}

          {workflowChoice && (
            <div className="receber-settlement receber-scan-choice">
              <strong>O lançamento não cobre o saldo</strong>
              <p>
                Recebido {formatCurrency(workflowChoice.payment)} de {formatCurrency(workflowChoice.remaining)}.
                Falta {formatCurrency(workflowChoice.shortfall)}.
              </p>
              <div className="receber-settlement-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={Boolean(workflowLinkingId)}
                  onClick={() => linkWorkflowMatch(workflowChoice.item, workflowChoice.match, 'partial')}
                >
                  Deixar em aberto
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={Boolean(workflowLinkingId)}
                  onClick={() => linkWorkflowMatch(workflowChoice.item, workflowChoice.match, 'full_with_adjustment')}
                >
                  Quitar com abatimento
                </button>
              </div>
            </div>
          )}

          <div className="receber-scan-list">
            {(workflowScan?.items || []).map((item) => (
              <article key={item.transaction.id} className="receber-scan-card">
                <header>
                  <div>
                    <strong>{formatCurrency(item.transaction.amount)}</strong>
                    <p>{item.transaction.description || 'Sem descrição'}</p>
                  </div>
                  <span>
                    {item.transaction.transactionDate ? formatDate(item.transaction.transactionDate) : 'Sem data'}
                  </span>
                </header>
                {item.exact.length > 0 && (
                  <div className="receber-scan-group">
                    <h4>Valores exatos</h4>
                    {item.exact.map((match) => (
                      <div key={`exact-${item.transaction.id}-${match.id}`} className="receber-scan-match">
                        <div>
                          <strong>{match.customerName || match.description || `Conta ${match.id}`}</strong>
                          <small>
                            Saldo {formatCurrency(match.remaining)}
                            {match.dueDate ? ` · vence ${formatDate(match.dueDate)}` : ''}
                          </small>
                        </div>
                        <div className="receber-scan-match-actions">
                          <button
                            type="button"
                            title="Ver detalhes"
                            onClick={() => openWorkflowDetails(match)}
                          >
                            <FileSearch size={16} />
                          </button>
                          <button
                            type="button"
                            className="btn-primary"
                            disabled={workflowLinkingId === `${item.transaction.id}-${match.id}`}
                            onClick={() => linkWorkflowMatch(item, match)}
                          >
                            Vincular
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {item.approximate.length > 0 && (
                  <div className="receber-scan-group">
                    <h4>Valores aproximados</h4>
                    {item.approximate.map((match) => (
                      <div key={`approx-${item.transaction.id}-${match.id}`} className="receber-scan-match">
                        <div>
                          <strong>{match.customerName || match.description || `Conta ${match.id}`}</strong>
                          <small>
                            Saldo {formatCurrency(match.remaining)} · diferença {formatCurrency(match.difference)}
                            {match.dueDate ? ` · vence ${formatDate(match.dueDate)}` : ''}
                          </small>
                        </div>
                        <div className="receber-scan-match-actions">
                          <button
                            type="button"
                            title="Ver detalhes"
                            onClick={() => openWorkflowDetails(match)}
                          >
                            <FileSearch size={16} />
                          </button>
                          <button
                            type="button"
                            className="btn-secondary"
                            disabled={workflowLinkingId === `${item.transaction.id}-${match.id}`}
                            onClick={() => linkWorkflowMatch(item, match)}
                          >
                            Vincular
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </article>
            ))}
            {workflowScan && !workflowLoading && workflowScan.items.length === 0 && (
              <p className="receber-scan-status">Nenhum lançamento do extrato combina com as contas em aberto desta conta.</p>
            )}
          </div>
        </div>
      </Modal>

      {/* Modal de Detalhes */}
      <Modal
        isOpen={detalhesModalOpen}
        onClose={() => {
          setDetalhesModalOpen(false)
          setContaDetalhes(null)
        }}
        title="Detalhes da Conta a Receber"
        size="large"
        overlayClassName="receber-details-overlay"
        footer={
          <button
            className="btn-secondary"
            onClick={() => {
              setDetalhesModalOpen(false)
              setContaDetalhes(null)
            }}
          >
            Fechar
          </button>
        }
      >
        {contaDetalhes && (
          <div style={{ padding: '1rem 0' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem', marginBottom: '1.5rem' }}>
              {/* Informações Básicas */}
              <div className="detalhes-section">
                <h3 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: '600', color: 'var(--text-primary, #1f2937)' }}>
                  Informações Básicas
                </h3>
                <div className="detalhes-item">
                  <span className="detalhes-label">ID:</span>
                  <span className="detalhes-value">{contaDetalhes.id}</span>
                </div>
                <div className="detalhes-item">
                  <span className="detalhes-label">Número:</span>
                  <span className="detalhes-value">{contaDetalhes.number || '-'}</span>
                </div>
                <div className="detalhes-item">
                  <span className="detalhes-label">Descrição:</span>
                  <span className="detalhes-value">{contaDetalhes.description || contaDetalhes.observation || '-'}</span>
                </div>
                <div className="detalhes-item">
                  <span className="detalhes-label">Status:</span>
                  <span className="detalhes-value">
                    <div className="contas-receber-status-cell">
                      {getStatusBadge(contaDetalhes.status, contaDetalhes.due_date)}
                      {renderBoletoBadge(contaDetalhes)}
                    </div>
                  </span>
                </div>
                {getLatestBoleto(contaDetalhes) && (
                  <div className="detalhes-item">
                    <span className="detalhes-label">Boleto:</span>
                    <span className="detalhes-value">
                      <div className="detalhes-boleto-info">
                        <span>{getBoletoStatusConfig(getLatestBoleto(contaDetalhes).status).label}</span>
                        {getLatestBoleto(contaDetalhes).due_date && (
                          <span className="detalhes-boleto-meta">
                            Vencimento: {formatDate(getLatestBoleto(contaDetalhes).due_date)}
                          </span>
                        )}
                        {getLatestBoleto(contaDetalhes).created_at && (
                          <span className="detalhes-boleto-meta">
                            Emitido em: {formatDate(getLatestBoleto(contaDetalhes).created_at)}
                          </span>
                        )}
                      </div>
                    </span>
                  </div>
                )}
              </div>

              {/* Valores */}
              <div className="detalhes-section">
                <h3 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: '600', color: 'var(--text-primary, #1f2937)' }}>
                  Valores
                </h3>
                <div className="detalhes-item">
                  <span className="detalhes-label">Valor Total:</span>
                  <span className="detalhes-value" style={{ fontWeight: '600', color: 'var(--color-primary, #3b82f6)' }}>
                    {formatCurrency(contaDetalhes.amount)}
                  </span>
                </div>
                <div className="detalhes-item">
                  <span className="detalhes-label">Valor Pago:</span>
                  <span className="detalhes-value" style={{ color: 'var(--color-success, #10b981)' }}>
                    {formatCurrency(contaDetalhes.paid_amount || 0)}
                  </span>
                </div>
                <div className="detalhes-item">
                  <span className="detalhes-label">Desconto:</span>
                  <span className="detalhes-value">{formatCurrency(contaDetalhes.discount_amount || 0)}</span>
                </div>
                <div className="detalhes-item">
                  <span className="detalhes-label">Juros:</span>
                  <span className="detalhes-value">{formatCurrency(contaDetalhes.interest_amount || 0)}</span>
                </div>
                <div className="detalhes-item">
                  <span className="detalhes-label">Multa:</span>
                  <span className="detalhes-value">{formatCurrency(contaDetalhes.fine_amount || 0)}</span>
                </div>
              </div>

              {/* Datas */}
              <div className="detalhes-section">
                <h3 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: '600', color: 'var(--text-primary, #1f2937)' }}>
                  Datas
                </h3>
                <div className="detalhes-item">
                  <span className="detalhes-label">Vencimento:</span>
                  <span className="detalhes-value">{formatDate(contaDetalhes.due_date)}</span>
                </div>
                <div className="detalhes-item">
                  <span className="detalhes-label">Data de Pagamento:</span>
                  <span className="detalhes-value">{formatDate(contaDetalhes.payment_date) || '-'}</span>
                </div>
                <div className="detalhes-item">
                  <span className="detalhes-label">Criado em:</span>
                  <span className="detalhes-value">{formatDate(contaDetalhes.created_at)}</span>
                </div>
                <div className="detalhes-item">
                  <span className="detalhes-label">Atualizado em:</span>
                  <span className="detalhes-value">{formatDate(contaDetalhes.updated_at)}</span>
                </div>
              </div>

              {/* Origem e Relacionamentos */}
              <div className="detalhes-section">
                <h3 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: '600', color: 'var(--text-primary, #1f2937)' }}>
                  Origem e Relacionamentos
                </h3>
                <div className="detalhes-item">
                  <span className="detalhes-label">Cliente:</span>
                  <span className="detalhes-value">
                    {contaDetalhes.customer_name || '-'}
                    {contaDetalhes.customer_cpf_cnpj && (
                      <span style={{ fontSize: '0.875rem', color: 'var(--text-tertiary, #6b7280)', marginLeft: '0.5rem' }}>
                        ({contaDetalhes.customer_cpf_cnpj})
                      </span>
                    )}
                  </span>
                </div>
                <div className="detalhes-item">
                  <span className="detalhes-label">Funcionário:</span>
                  <span className="detalhes-value">{contaDetalhes.user_name || '-'}</span>
                </div>
                <div className="detalhes-item">
                  <span className="detalhes-label">Origem:</span>
                  <span className="detalhes-value">{getOriginName(contaDetalhes)}</span>
                </div>
                {contaDetalhes.reference_type && (
                  <div className="detalhes-item">
                    <span className="detalhes-label">Tipo de Referência:</span>
                    <span className="detalhes-value">{contaDetalhes.reference_type}</span>
                  </div>
                )}
                {contaDetalhes.reference_id && (
                  <div className="detalhes-item">
                    <span className="detalhes-label">ID de Referência:</span>
                    <span className="detalhes-value">{contaDetalhes.reference_id}</span>
                  </div>
                )}
                {contaDetalhes.nfe_number && (
                  <div className="detalhes-item">
                    <span className="detalhes-label">NF-e:</span>
                    <span className="detalhes-value" style={{ fontWeight: '600' }}>
                      {contaDetalhes.nfe_number}
                    </span>
                  </div>
                )}
                {getInstallmentInfo(contaDetalhes) && (
                  <div className="detalhes-item">
                    <span className="detalhes-label">Parcela:</span>
                    <span className="detalhes-value" style={{ fontWeight: '600' }}>
                      {getInstallmentInfo(contaDetalhes).current} de {getInstallmentInfo(contaDetalhes).total}
                    </span>
                  </div>
                )}
              </div>

              {/* Categorização */}
              <div className="detalhes-section">
                <h3 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: '600', color: 'var(--text-primary, #1f2937)' }}>
                  Categorização
                </h3>
                <div className="detalhes-item">
                  <span className="detalhes-label">Categoria:</span>
                  <span className="detalhes-value">{contaDetalhes.category_name || '-'}</span>
                </div>
                <div className="detalhes-item">
                  <span className="detalhes-label">Centro de Custo:</span>
                  <span className="detalhes-value">{contaDetalhes.cost_center_name || '-'}</span>
                </div>
                <div className="detalhes-item">
                  <span className="detalhes-label">Conta Financeira:</span>
                  <span className="detalhes-value">
                    {contaDetalhes.financial_account_name || '-'}
                    {contaDetalhes.financial_account_code && (
                      <span style={{ fontSize: '0.875rem', color: 'var(--text-tertiary, #6b7280)', marginLeft: '0.5rem' }}>
                        ({contaDetalhes.financial_account_code})
                      </span>
                    )}
                  </span>
                </div>
              </div>

              {/* Auditoria */}
              <div className="detalhes-section">
                <h3 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: '600', color: 'var(--text-primary, #1f2937)' }}>
                  Auditoria
                </h3>
                <div className="detalhes-item">
                  <span className="detalhes-label">Criado por:</span>
                  <span className="detalhes-value">{contaDetalhes.created_by_name || '-'}</span>
                </div>
                {contaDetalhes.linked_transactions && contaDetalhes.linked_transactions.length > 0 && (
                  <div className="detalhes-item" style={{ gridColumn: '1 / -1' }}>
                    <span className="detalhes-label">Transações Vinculadas:</span>
                    <div style={{ marginTop: '0.5rem' }}>
                      {contaDetalhes.linked_transactions.map((transaction, idx) => (
                        <div key={idx} style={{ 
                          padding: '0.5rem', 
                          background: 'var(--bg-secondary, #f3f4f6)', 
                          borderRadius: '4px',
                          marginBottom: '0.5rem'
                        }}>
                          <div style={{ fontSize: '0.875rem' }}>
                            <strong>Descrição:</strong> {transaction.description || '-'}
                          </div>
                          <div style={{ fontSize: '0.875rem', color: 'var(--text-tertiary, #6b7280)' }}>
                            <strong>Valor:</strong> {formatCurrency(transaction.amount || 0)} | 
                            <strong> Data:</strong> {formatDate(transaction.transaction_date)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Observações */}
            {(contaDetalhes.observation || contaDetalhes.observations) && (
              <div className="detalhes-section" style={{ marginTop: '1.5rem' }}>
                <h3 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: '600', color: 'var(--text-primary, #1f2937)' }}>
                  Observações
                </h3>
                <div style={{ 
                  padding: '1rem', 
                  background: 'var(--bg-secondary, #f3f4f6)', 
                  borderRadius: '4px',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {contaDetalhes.observation || contaDetalhes.observations}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Modal de Importação Excel */}
      <Modal
        isOpen={importModalOpen}
        onClose={handleImportClose}
        title="Importar Contas a Receber"
        size="large"
        footer={
          <>
            {importStep === 'upload' && (
              <>
                <button className="btn-secondary" onClick={handleImportClose}>Cancelar</button>
                <button className="btn-primary" onClick={handleImportPreview} disabled={!importFile || importLoading}>
                  {importLoading ? 'Processando...' : 'Visualizar'}
                </button>
              </>
            )}
            {importStep === 'preview' && (
              <>
                <button className="btn-secondary" onClick={() => { setImportStep('upload'); setImportPreview(null) }}>Voltar</button>
                <button className="btn-primary" onClick={handleImportExecute} disabled={importLoading || !importPreview?.validRows}>
                  {importLoading ? 'Importando...' : `Importar ${importPreview?.validRows || 0} contas`}
                </button>
              </>
            )}
            {importStep === 'result' && (
              <button className="btn-primary" onClick={handleImportClose}>Fechar</button>
            )}
          </>
        }
      >
        {importStep === 'upload' && (
          <div className="import-upload-area">
            <input
              ref={importFileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleImportFileSelect}
              style={{ display: 'none' }}
            />
            <div
              className="import-dropzone"
              onDrop={handleImportDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => importFileInputRef.current?.click()}
            >
              <FileSpreadsheet size={48} strokeWidth={1.5} />
              <p><strong>Arraste o arquivo Excel aqui</strong></p>
              <p>ou clique para selecionar</p>
              <span className="import-dropzone-hint">Formatos aceitos: .xlsx, .xls</span>
            </div>
            {importFile && (
              <div className="import-file-selected">
                <FileSpreadsheet size={16} />
                <span>{importFile.name}</span>
                <button onClick={() => setImportFile(null)}><X size={14} /></button>
              </div>
            )}
            <div className="import-instructions">
              <h4>Formato esperado da planilha:</h4>
              <table className="import-format-table">
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Nome/Razão social</th>
                    <th>Descrição</th>
                    <th>Parcela</th>
                    <th>Data vencimento</th>
                    <th>Valor parcela</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>15646</td>
                    <td>KAREN ILKA RODRIGUES MAURENTE</td>
                    <td>Ref. a venda cód. 24670.2</td>
                    <td>1/1</td>
                    <td>04/05/2025</td>
                    <td>2,05</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {importStep === 'preview' && importPreview && (
          <div className="import-preview-area">
            <div className="import-preview-summary">
              <div className="import-stat">
                <span className="import-stat-value">{importPreview.totalRows}</span>
                <span className="import-stat-label">Total de linhas</span>
              </div>
              <div className="import-stat success">
                <span className="import-stat-value">{importPreview.validRows}</span>
                <span className="import-stat-label">Válidas</span>
              </div>
              {importPreview.errorRows > 0 && (
                <div className="import-stat error">
                  <span className="import-stat-value">{importPreview.errorRows}</span>
                  <span className="import-stat-label">Com erros</span>
                </div>
              )}
            </div>

            <div className="import-preview-table-wrapper">
              <table className="import-preview-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Código</th>
                    <th>Cliente</th>
                    <th>Descrição</th>
                    <th>Parcela</th>
                    <th>Vencimento</th>
                    <th>Valor</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {importPreview.rows.map((row) => (
                    <tr key={row.rowNumber} className={!row.valid ? 'import-row-error' : row.warnings.length > 0 ? 'import-row-warning' : ''}>
                      <td>{row.rowNumber}</td>
                      <td>{row.customerCode}</td>
                      <td>
                        {row.customerName}
                        {(row.customerCode || row.customerName) && !row.customerId && (
                          <span className="import-badge warn" title="Cliente não encontrado">!</span>
                        )}
                      </td>
                      <td>{row.description}</td>
                      <td>{row.installment}</td>
                      <td>{row.dueDateFormatted}</td>
                      <td>{row.amountFormatted}</td>
                      <td>
                        {row.valid ? (
                          <span className="import-badge ok"><CheckCircle2 size={12} /> OK</span>
                        ) : (
                          <span className="import-badge err" title={row.errors.join(', ')}><AlertCircle size={12} /> Erro</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {importStep === 'result' && importResult && (
          <div className="import-result-area">
            <div className="import-result-icon">
              <CheckCircle2 size={48} />
            </div>
            <h3>Importação Concluída</h3>
            <div className="import-result-stats">
              <p><strong>{importResult.created}</strong> contas criadas com sucesso</p>
              {importResult.skipped > 0 && <p><strong>{importResult.skipped}</strong> linhas ignoradas (erros de validação)</p>}
              {importResult.errors?.length > 0 && (
                <div className="import-result-errors">
                  <p>Erros durante a importação:</p>
                  <ul>
                    {importResult.errors.map((err, i) => (
                      <li key={i}>Linha {err.rowNumber}: {err.error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      <SelectPersonModal
        isOpen={showPersonModal}
        onClose={() => setShowPersonModal(false)}
        onSelect={handleSelectPerson}
        selectedType={formData.personType}
        selectedId={formData.personId}
        allowedTypes={['customer', 'user']}
      />

      {/* Toast para notificações */}
      <Modal
        isOpen={Boolean(linkReview)}
        onClose={closeLinkReview}
        title="Vínculo do extrato"
        size="medium"
        footer={
          <>
            <button className="btn-secondary" onClick={closeLinkReview}>Fechar</button>
            {linkReview && !linkSwapChoice && (
              <button className="btn-secondary" onClick={confirmUnlinkReconciliation}>
                Desvincular
              </button>
            )}
          </>
        }
      >
        {linkReview && (
          <div className="receber-settlement">
            <div className="receber-settlement-details">
              <div>
                <span>Saldo em aberto</span>
                <strong>{formatCurrency(receivableRemaining(linkReview.account))}</strong>
              </div>
              <div>
                <span>Valor da conta</span>
                <strong>{formatCurrency(linkReview.account.amount)}</strong>
              </div>
              <div>
                <span>Valor vinculado</span>
                <strong>{formatCurrency(linkReview.transaction.amount)}</strong>
              </div>
            </div>
            <div className="receber-link-card">
              <div className="receber-link-card-head">
                <h4>Transação vinculada</h4>
                <button
                  type="button"
                  className="receber-scan-view"
                  title="Ver detalhes"
                  onClick={() => openWorkflowDetails(linkReview.account)}
                >
                  <FileSearch size={16} />
                </button>
              </div>
              <p>{linkReview.transaction.description || 'Sem descrição'}</p>
              <p>
                {linkReview.transaction.transaction_date ? formatDate(linkReview.transaction.transaction_date) : 'Sem data'}
                {linkReview.transaction.fit_id ? ` · ${linkReview.transaction.fit_id}` : ''}
              </p>
            </div>
            {linkSwapChoice ? (
              <div className="receber-settlement-options">
                <div>
                  <h4>Deixar em aberto</h4>
                  <p>
                    Troca o vínculo por {formatCurrency(linkSwapChoice.payment)} e mantém {formatCurrency(linkSwapChoice.shortfall)} pendente.
                  </p>
                  <button type="button" className="btn-primary" onClick={() => replaceLinkedTransaction(linkSwapChoice.candidate, 'partial')}>
                    Confirmar pagamento parcial
                  </button>
                </div>
                <div>
                  <h4>Quitar com abatimento</h4>
                  <p>Encerra a conta e lança abatimento de {formatCurrency(linkSwapChoice.shortfall)}.</p>
                  <button type="button" className="btn-primary" onClick={() => replaceLinkedTransaction(linkSwapChoice.candidate, 'full_with_adjustment')}>
                    Quitar como pagamento final
                  </button>
                </div>
                <button type="button" className="btn-secondary" onClick={() => setLinkSwapChoice(null)}>Voltar</button>
              </div>
            ) : (
              <>
                <button type="button" className="btn-primary receber-link-search" onClick={searchEquivalentTransactions} disabled={linkSuggestionsLoading}>
                  {linkSuggestionsLoading ? 'Buscando no extrato...' : 'Buscar equivalentes no extrato'}
                </button>
                {linkSuggestions && (
                  <div className="receber-link-results">
                    <h4>Valores exatos</h4>
                    {linkSuggestions.exact?.length ? linkSuggestions.exact.map((item) => (
                      <button key={item.id} type="button" className="receber-link-result" onClick={() => replaceLinkedTransaction(item)}>
                        <span>{item.description || 'Lançamento do extrato'}</span>
                        <strong>{formatCurrency(item.amount)}</strong>
                        <small>{item.transactionDate ? formatDate(item.transactionDate) : ''}</small>
                      </button>
                    )) : <p>Nenhum lançamento com o mesmo valor.</p>}
                    <h4>Valores aproximados</h4>
                    {linkSuggestions.approximate?.length ? linkSuggestions.approximate.map((item) => (
                      <button key={item.id} type="button" className="receber-link-result" onClick={() => replaceLinkedTransaction(item)}>
                        <span>{item.description || 'Lançamento do extrato'}</span>
                        <strong>{formatCurrency(item.amount)}</strong>
                        <small>
                          {item.transactionDate ? formatDate(item.transactionDate) : ''}
                          {` · diferença ${formatCurrency(item.difference)}`}
                        </small>
                      </button>
                    )) : <p>Nenhum lançamento com valor próximo.</p>}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </Modal>

      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={hideToast}
      />

      {generatingPDF && (
        <div className="ar-pdf-overlay">
          <div className="ar-pdf-overlay-card">
            <Download size={28} color="#10b981" />
            <h4>Gerando PDF</h4>
            <p>Preparando relatório de contas a receber...</p>
          </div>
        </div>
      )}

      {exportPdfAccounts.length > 0 && (
        <div className="ar-pdf-offscreen" aria-hidden>
          <AccountsReceivablePdfCanvas
            ref={pdfRef}
            accounts={exportPdfAccounts}
            filters={filters}
          />
        </div>
      )}
    </div>
  )
}

export default ContasReceberPage

